import UserContainer from "./UsersStyle";
import Container from "./UsersTableStyle";
import { PhoneMask, CPFMask, CNPJMask, customDateTimeFormat } from "../../utils";
import TwoButtonsModal from "../../shared/Bootstrap/TwoButtonsModal";
import api from "../../services/api";
import Spinner from "../../shared/Spinner/Spinner";

import { FaUserMinus, FaSortUp, FaSortDown } from "react-icons/fa";
import { useContext, useEffect, useState } from "react";
import { UserData } from "./Users";
import { useStateIfMounted } from "use-state-if-mounted";
import AuthContext from "../../contexts/authContext";
import PageTitle from "../../components/PageTitle";

export default function UsersTable(props) {
  const { LogOut } = useContext(AuthContext);
  const [loadingData, setLoadingData] = useStateIfMounted(false);
  const [users, setUsers] = useStateIfMounted([] as UserData[]);
  const [filteredUsers, setFilteredUsers] = useState([] as UserData[]);
  const [nameFilter, setNameFilter] = useState("");
  const [idFilter, setIdFilter] = useState("");
  const [cpfFilter, setCpfFilter] = useState(""); // New CPF filter state
  const [sortBy, setSortBy] = useState<"id" | "name" | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null
  );

  const [showPopupDeletingUser, setShowPopupDeletingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [userToDelete, setUserToDelete] = useState({} as any);

  useEffect(() => {
    LoadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function LoadUsers() {
    try {
      setLoadingData(true);
      const request = await api.get("/users");
      const users = request.data;
      setLoadingData(false);
      setUsers(users);
      setFilteredUsers(users);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        LogOut();
        console.error("Não esta logado!");
        return;
      }
      setLoadingData(false);
    }
  }

  useEffect(() => {
    let updatedUsers = [...users];

    updatedUsers = updatedUsers.filter((user) => {
      const matchesName = user.name
        .toLowerCase()
        .includes(nameFilter.toLowerCase());
      const matchesId =
        idFilter === "" || user.accountId.toString().includes(idFilter);
      const matchesCpf = // New CPF filter logic
        cpfFilter === "" ||
        (user.cpf &&
          user.cpf.replace(/\D/g, "").includes(cpfFilter.replace(/\D/g, ""))) ||
        (user.cnpj &&
          user.cnpj.replace(/\D/g, "").includes(cpfFilter.replace(/\D/g, "")));
      return matchesName && matchesId && matchesCpf;
    });

    if (sortBy && sortDirection) {
      updatedUsers.sort((a, b) => {
        if (sortBy === "id") {
          return sortDirection === "asc"
            ? a.accountId - b.accountId
            : b.accountId - a.accountId;
        } else if (sortBy === "name") {
          return sortDirection === "asc"
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        }
        return 0;
      });
    }

    setFilteredUsers(updatedUsers);
  }, [nameFilter, idFilter, cpfFilter, users, sortBy, sortDirection]); // Added cpfFilter to dependencies

  function toggleSort(criteria: "id" | "name") {
    if (sortBy === criteria) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(criteria);
      setSortDirection("asc");
    }
  }

  async function OnDeleteUser() {
    setDeletingUser(true);
    try {
      const deleteRequest = await api.delete("/users/delete", {
        data: { accountIdToDelete: userToDelete.accountId },
      });
      const deleteData = deleteRequest.data;
      console.log(deleteData);
      await LoadUsers();
      DisablePopupDeletingUser();
    } catch (error) {
      console.error(error);
      DisablePopupDeletingUser();
    }
  }

  function DisablePopupDeletingUser() {
    setDeletingUser(false);
    setShowPopupDeletingUser(false);
  }

  function ClosePopupDeletingUser() {
    setShowPopupDeletingUser(false);
  }

  function GoToUserDetail(user) {
    props.history.push(`/users/${user.userId}`);
  }

  return (
    <UserContainer>
      <PageTitle title="Usuários" />
      <Container>
        <Spinner spinning={loadingData} />
        <TwoButtonsModal
          show={showPopupDeletingUser}
          onHide={ClosePopupDeletingUser}
          loading={deletingUser}
          title="Deleção de usuário!"
          body="Você está tentando deletar um usuário, essa ação é irreversível!"
          secondaryText="Fechar"
          secondaryOnClick={ClosePopupDeletingUser}
          secondaryDisabled={deletingUser}
          primaryText="Deletar"
          primaryOnClick={OnDeleteUser}
          primaryDisabled={deletingUser}
        />

        <section className="table-title">
          <h1>Clientes</h1>
        </section>

        <div className="filter-section">
          <div className="filter-input">
            <label>Filtrar por ID: </label>
            <input
              type="text"
              value={idFilter}
              onChange={(e) => setIdFilter(e.target.value)}
              placeholder="Digite o ID"
            />
          </div>
          <div className="filter-input">
            <label>Filtrar por Nome: </label>
            <input
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Digite o nome"
            />
          </div>
          <div className="filter-input">
            <label>Filtrar por CPF/CNPJ: </label>
            <input
              type="text"
              value={cpfFilter}
              onChange={(e) => setCpfFilter(e.target.value)}
              placeholder="Digite o CPF ou CNPJ"
            />
          </div>
        </div>

        <table className="table table-striped table-bordered table-hover main-table">
          <thead>
            <tr>
              <th>
                Id
                <button
                  className="sort-button"
                  onClick={() => toggleSort("id")}
                >
                  {sortBy === "id" && sortDirection === "asc" ? (
                    <FaSortUp />
                  ) : (
                    <FaSortDown />
                  )}
                </button>
              </th>
              <th>
                Nome
                <button
                  className="sort-button"
                  onClick={() => toggleSort("name")}
                >
                  {sortBy === "name" && sortDirection === "asc" ? (
                    <FaSortUp />
                  ) : (
                    <FaSortDown />
                  )}
                </button>
              </th>
              <th>Email</th>
              <th>CPF/CNPJ</th>
              <th>Phone</th>
              <th>Empreendimento</th>
              <th>Cadastro</th>
              <th>Acesso</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length > 0 &&
              filteredUsers.map((user) => {
                return (
                  <tr key={user.accountId}>
                    <td
                      onClick={() => GoToUserDetail(user)}
                      className="clickable"
                    >
                      {user.accountId}
                    </td>
                    <td
                      onClick={() => GoToUserDetail(user)}
                      className="clickable"
                    >
                      {user.name}
                    </td>
                    <td
                      onClick={() => GoToUserDetail(user)}
                      className="clickable"
                    >
                      {user.email}
                    </td>
                    <td
                      onClick={() => GoToUserDetail(user)}
                      className="clickable"
                    >
                      {CPFMask(user.cpf) || CNPJMask(user.cnpj)}
                    </td>
                    <td
                      onClick={() => GoToUserDetail(user)}
                      className="clickable"
                    >
                      {PhoneMask(user.phone)}
                    </td>
                    <td
                      onClick={() => GoToUserDetail(user)}
                      className="clickable"
                    >
                      {user.enterprise}
                    </td>
                    <td>
                      {user.createdAt ? customDateTimeFormat(user.createdAt, ":") : "Sem data"}
                    </td>

                    <td>
                      {user.lastLoginDate ? customDateTimeFormat(user.lastLoginDate, ":") : "Não acessou"}
                    </td>
                    <td>
                      <div
                        className="action"
                        onClick={() => {
                          setShowPopupDeletingUser(true);
                          setUserToDelete(user);
                        }}
                      >
                        <FaUserMinus className="icon" />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </Container>
    </UserContainer>
  );
}
